import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  email: string;
  name: string;
  nameKo?: string;
  avatarUrl?: string;
  avatarPreset?: string;
  avatar_preset?: string;
  bio?: string;
  location?: string;
  countryCode?: string;
  country_code?: string;
  life_stage?: string;
  language: 'en' | 'ko';
  points: number;
  tokens: number;
  level: number;
  isVerified: boolean;
  createdAt: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  login: (user: User, token: string) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  updateProfile: (data: Partial<User>) => Promise<void>;
  addPoints: (points: number) => void;
  addTokens: (tokens: number) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      
      setToken: (token) => set({ token }),
      
      setLoading: (isLoading) => set({ isLoading }),

      login: (user, token) => set({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
      }),

      logout: () => set({
        user: null,
        token: null,
        isAuthenticated: false,
      }),

      updateUser: (updates) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...updates } });
        }
      },

      updateProfile: async (data) => {
        const token = get().token || localStorage.getItem('auth_token');
        const API_URL = import.meta.env.VITE_API_URL || '';

        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            credentials: 'include',
            body: JSON.stringify(data),
          });

          if (response.ok) {
            const result = await response.json();
            set({ user: result.user });
          }
        } catch (error) {
          console.error('Update profile error:', error);
        }
      },

      addPoints: (points) => {
        const currentUser = get().user;
        if (currentUser) {
          const newPoints = currentUser.points + points;
          const newLevel = calculateLevel(newPoints);
          set({ 
            user: { 
              ...currentUser, 
              points: newPoints,
              level: newLevel,
            } 
          });
        }
      },

      addTokens: (tokens) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ 
            user: { 
              ...currentUser, 
              tokens: currentUser.tokens + tokens,
            } 
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Helper function to calculate level based on points
function calculateLevel(points: number): number {
  const thresholds = [0, 100, 300, 600, 1000, 2000, 5000, 10000, 25000, 50000];
  let level = 1;
  for (let i = 0; i < thresholds.length; i++) {
    if (points >= thresholds[i]) {
      level = i + 1;
    } else {
      break;
    }
  }
  return level;
}
